import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  AlertTriangle,
  FileEdit,
  MessageCircleQuestion,
  ChevronRight,
  Zap,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

const benefits = [
  {
    icon: AlertTriangle,
    title: "Flags ambiguity and missing assumptions",
    description:
      "Reqqy identifies vague terms, undefined scope, and implicit dependencies before they cause confusion.",
  },
  {
    icon: FileEdit,
    title: "Rewrites requirements for clarity",
    description: "Get improved versions of your requirements with explicit acceptance criteria and clear assumptions.",
  },
  {
    icon: MessageCircleQuestion,
    title: "Generates clarifying questions",
    description: "Know exactly what engineers will ask before the sprint even starts.",
  },
];

const phrases = ["unclear requirements", "sprint delays", "ambiguous specs", "scope creep"];

function TypewriterText() {
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [deleting, setDeleting] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const current = phrases[phraseIdx];
    if (!deleting && displayed.length < current.length) {
      timeoutRef.current = setTimeout(() => setDisplayed(current.slice(0, displayed.length + 1)), 60);
    } else if (!deleting && displayed.length === current.length) {
      timeoutRef.current = setTimeout(() => setDeleting(true), 2000);
    } else if (deleting && displayed.length > 0) {
      timeoutRef.current = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 35);
    } else if (deleting && displayed.length === 0) {
      setDeleting(false);
      setPhraseIdx((i) => (i + 1) % phrases.length);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [displayed, deleting, phraseIdx]);

  return (
    <span className="text-gradient relative">
      {displayed}
      <span className="animate-pulse">|</span>
    </span>
  );
}

export function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* === Premium layered background === */}
      {/* Base dark radial vignette */}
      {/*<div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_hsl(var(--background))_0%,_hsl(0_0%_0%)_100%)]" />

      {/* Primary glow — top center, large and diffuse */}
      <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-primary/[0.12] blur-[160px] animate-pulse-slow" />

      {/* Accent glow — bottom left, offset */}
      <div
        className="absolute bottom-[-10%] left-[15%] w-[500px] h-[500px] rounded-full bg-accent/[0.08] blur-[140px] animate-pulse-slow"
        style={{ animationDelay: "2s" }}
      />

      {/* Secondary accent — right side */}
      <div
        className="absolute top-[30%] right-[5%] w-[350px] h-[350px] rounded-full bg-primary/[0.06] blur-[120px] animate-pulse-slow"
        style={{ animationDelay: "4s" }}
      />

      {/* Noise texture overlay for depth */}
      <div className="absolute inset-0 opacity-[0.035] mix-blend-overlay" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }} />

      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage: `linear-gradient(hsl(var(--primary) / 0.4) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary) / 0.4) 1px, transparent 1px)`,
          backgroundSize: "80px 80px",
        }}
      />

      {/* Top edge gradient line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

      {/* === Content === */}
      <div className="container relative z-10 px-4 md:px-6 py-24 md:py-32">
        <div className="max-w-6xl mx-auto text-center">
          {/* Live badge — refined pill */}
          <div className="hero-stagger-1 inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-primary/20 bg-primary/[0.06] backdrop-blur-md text-sm mb-10">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-reqqy-success opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-reqqy-success" />
            </span>
            <span className="text-muted-foreground tracking-wide">AI-powered · Now in beta</span>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/60" />
          </div>

          {/* Headline — dramatic sizing with tight leading */}
          <h1 className="hero-stagger-2 text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] xl:text-[6.5rem] font-display font-bold tracking-[-0.03em] mb-7 leading-[1.02]">
            Stop shipping
            <br />
            <TypewriterText />
          </h1>

          {/* Subheading — wider, more breathable */}
          <p className="hero-stagger-3 text-lg md:text-xl lg:text-[1.35rem] text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed">
            Reqqy reviews your requirements before engineering sees them.{" "}
            <br className="hidden sm:block" />
            <span className="text-foreground font-medium">No more "what do you mean by...?" in sprint planning.</span>
          </p>

          {/* CTAs — premium button treatment */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up">
            <Button
              asChild
              size="lg"
              className="relative group h-12 px-8 text-base font-semibold gradient-primary text-primary-foreground overflow-hidden transition-all duration-300 hover:shadow-[0_0_40px_-8px_hsl(var(--primary)/0.5)]"
            >
              <Link to="/dashboard/new-run?mode=review">
                <span className="relative z-10 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Review my requirements — free
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
                {/* Shimmer sweep */}
                <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="h-12 px-8 text-base border-border/60 hover:border-primary/40 transition-all duration-300">
              <Link to="/pricing">See Pricing</Link>
            </Button>
          </div>

          {/* Trust signals */}
          <p className="hero-stagger-5 text-xs text-muted-foreground/70 mt-5 tracking-wide">
            No credit card required · 10 free credits · Setup in 30 seconds
          </p>

          {/* Benefits row — glass cards with refined styling */}
          <div className="hero-stagger-6 mt-16">
            <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {benefits.map((benefit, i) => (
                <div
                  key={benefit.title}
                  className="group flex items-start gap-3 p-5 rounded-xl border border-border/30 bg-card/[0.4] backdrop-blur-md text-left transition-all duration-300 hover:border-primary/20 hover:bg-card/[0.6] hover:shadow-[0_8px_30px_-12px_hsl(var(--primary)/0.15)]"
                >
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors group-hover:bg-primary/15">
                    <benefit.icon className="w-4 h-4 text-primary" />
                  </div>
                  <p className="text-sm font-medium leading-snug text-foreground/90">{benefit.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade into next section */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
}
